import React, {useState} from 'react';
import {space, UpdateProviderSurveyRequest, UpdateProviderSurveyResponse} from "../../provider_api";
import './NewProviderSurvey.css';
import {useProviderProfile} from "../../ProviderProfileProvider";
import {SurveyPage} from "../../components/survey/SurveyComponents";
import {LabeledTextInput} from "../../components/input/LabeledTextInput";
import {MultiSelect} from "../../components/input/MultiSelectInput";
import {RadioSelect} from "../../components/input/RadioSelectInput";
import {findError} from "../../components/input/InputTypes";
import {useRpcSender} from "../../util/RpcSender";
import {subscriptionIdToString} from "../../util/ProviderUtil";
import {MembershipOption} from "./MembershipOption";
import {useNavigateWithParams} from "../../util/NavUtils";
import ProviderSurveyProto = space.kenko.proto.ProviderSurveyProto;
import IProviderSurveyProto = space.kenko.proto.IProviderSurveyProto;
import SurveySessionType = space.kenko.proto.SurveySessionType;
import IErrorProto = space.kenko.proto.IErrorProto;
import SurveyCurrentBookingPlatform = space.kenko.proto.SurveyCurrentBookingPlatform;
import SurveyTeamMembers = space.kenko.proto.SurveyTeamMembers;
import BusinessSubscriptionType = space.kenko.proto.BusinessSubscriptionType;
import {ActionButton} from '../../util/UI/ActionButton';
import {listingPlusBookingPrice, listingPrice} from "../../util/SubscriptionUtil";
import SurveyVirtualServices = space.kenko.proto.SurveyVirtualServices;
import {useUpdateProviderSurvey} from "../../util/RpcHooks/UpdateProviderSurveyRpc";

const BusinessOnboarding = () => {

  const {business} = useProviderProfile();
  const [currentStep, setCurrentStep] = useState(0);

  const updateProviderSurveyRpc = useUpdateProviderSurvey();

  // The results are kept as the survey proto. 
  const [results, setResults] = useState<IProviderSurveyProto>(
      business?.surveyProto ?? new ProviderSurveyProto({
        numLocations: 1,
        numPractitioners: 1
      }));
  const [errors, setErrors] = useState<IErrorProto[]>([]);

  // A top-level error that sits near the "continue" button.
  const [validationError, setValidationError] = useState<string>();

  const [billingPeriod, setBillingPeriod] = useState<'annual' | 'monthly'>('annual');

  const sessionTypes = [
    {value: SurveySessionType.PRIVATE_SESSIONS, label: 'Private 1:1 sessions'},
    {value: SurveySessionType.SEMI_PRIVATE_SESSIONS, label: 'Semi-private sessions (2+ clients)'},
    {value: SurveySessionType.GROUP_CLASSES, label: 'Group Classes'},
    {value: SurveySessionType.EVENTS, label: 'Events (e.g. workshops, retreats)'},
  ]

  const currentBookingPlatformOptions = [
    {value: SurveyCurrentBookingPlatform.YES_AND_HAPPY, label: "Yes, and I'm happy with it"},
    {value: SurveyCurrentBookingPlatform.YES_AND_OPEN, label: "Yes, but I'm open to switching"},
    {value: SurveyCurrentBookingPlatform.NO_BUT_LOOKING, label: "No, but I'm looking for one"},
    {value: SurveyCurrentBookingPlatform.NO_DONT_NEED, label: "No, I don't need one"},
  ]

  const virtualServiceOptions = [
    {value: SurveyVirtualServices.IN_PERSON_ONLY, label: 'In-person only'},
    {value: SurveyVirtualServices.IN_PERSON_AND_VIRTUAL, label: 'In-person and virtual'},
    {value: SurveyVirtualServices.VIRTUAL_ONLY, label: 'Virtual only'},
  ]

  const recommendedPlan = (): BusinessSubscriptionType => {
    // These are special strings in BusinessSubscription.java
    return (results.currentBookingPlatform == SurveyCurrentBookingPlatform.YES_AND_OPEN ||
        results.currentBookingPlatform == SurveyCurrentBookingPlatform.NO_BUT_LOOKING) ?
        BusinessSubscriptionType.LISTING_PLUS_BOOKING : BusinessSubscriptionType.LISTING;
  }

  const numLocations = results.numLocations;
  const numPractitioners = results.numPractitioners;

  const surveyPages: SurveyPage[] = [
    {
      title: "What's your business name?",
      subtitle: "This is the name that your clients will see. You can add your billing and legal name later.",
      content: <div className={'NewSurveyContentContainer'}>
        <LabeledTextInput label={'Business name'} inputType='text'
                          value={results.businessName}
                          error={findError(errors, 'businessName')}
                          onChange={v => {
                            setResults(results => {
                              return {...results, businessName: v};
                            })
                          }}/>

      </div>,
      validate: () => {
        if (!results.businessName || results.businessName.trim().length == 0) {
          setValidationError('Please check below for errors');
          setErrors([{path: 'businessName', error: 'Please enter a business name'}]);
          return false;
        }
        return true;
      }
    }, {
      title: "How many practitioners does your business have?",
      subtitle: "Include anyone who offers classes or services. " +
          "(e.g. practitioners, teachers).",
      content: <div className={'NewSurveyContentContainer'}>
        <LabeledTextInput
            label={'Number of practitioners'}
            inputType={'number'}
            value={results.numPractitioners?.toString()}
            onChange={v => {
              setResults(results => ({...results, numPractitioners: parseInt(v)}));
            }}/>
      </div>,
      validate: () => {
        if (!results.numPractitioners || results.numPractitioners <= 0 || results.numPractitioners >= 100) {
          setValidationError('Please enter a valid non-zero number');
          return false;
        }
        return true;
      }
    }, {
      title: "Do you offer in-person and/or virtual services?",
      content: <RadioSelect options={virtualServiceOptions}
                            value={results.virtualServices}
                            onChange={v => {
                              setResults(results => {
                                return {...results, virtualServices: v};
                              })
                            }}/>,
      validate: () => {
        if (!results.virtualServices) {
          setValidationError('Please select one');
          return false;
        }
        return true;
      }
    }, {
      title: "How many office locations does your business have?",
      content: <div className={'NewSurveyContentContainer'}>
        <LabeledTextInput
            inputType={'number'}
            label={'Number of locations'}
            value={results.numLocations?.toString()}
            onChange={v => {
              setResults(results => ({...results, numLocations: parseInt(v)}));
            }}/>
      </div>,
      validate: () => {
        if (!results.numLocations || results.numLocations <= 0 || results.numLocations >= 25) {
          setValidationError('Please enter a valid non-zero number');
          return false;
        }
        return true;
      },
      isPageVisible: () => {
        return results.virtualServices == SurveyVirtualServices.IN_PERSON_AND_VIRTUAL ||
            results.virtualServices == SurveyVirtualServices.IN_PERSON_ONLY;
      }
    }, {
      title: 'What type of sessions does your business offer?',
      subtitle: 'Select all that apply.',
      content: <MultiSelect options={sessionTypes}
                            value={results.sessionTypes || []}
                            onChange={v => {
                              setResults(results => {
                                return {...results, sessionTypes: v};
                              })
                            }}/>,
      validate: () => {
        if (!results.sessionTypes?.length) {
          setValidationError('Please select at least one option');
          return false;
        }
        return true;
      },
    },
    {
      title: 'Do you currently use a booking or scheduling platform?',
      subtitle: "This helps us determine whether Kensu's booking and scheduling platform could be helpful for your business.",
      content: <RadioSelect options={currentBookingPlatformOptions}
                            value={results.currentBookingPlatform}
                            onChange={v => {
                              setResults(results => {
                                return {
                                  ...results,
                                  currentBookingPlatform: v,
                                  chosenSubscription: recommendedPlan()
                                };
                              })
                            }}/>,
      validate: () => {
        if (!results.currentBookingPlatform) {
          setValidationError('Please select one');
          return false;
        }
        return true;
      },
    },
    {
      pageHeaderOverride: 'CONFIRMATION',
      title: <>Based on your business needs, we recommend the {subscriptionIdToString(recommendedPlan())} plan.</>,
      content: <div className={'SurveyOpeningPage'}>

        <div className={'ProviderToolAreaContent'}>

          <div className={'BillingPeriodOptions'}>
            <div className={billingPeriod == 'annual' ? 'BillingPeriodOption Highlighted' : 'BillingPeriodOption'}
                 onClick={() => setBillingPeriod('annual')}>Annual
            </div>
            <div className={billingPeriod == 'monthly' ? 'BillingPeriodOption Highlighted' : 'BillingPeriodOption'}
                 onClick={() => setBillingPeriod(('monthly'))}>Monthly
            </div>
          </div>

          <div style={{marginBottom: 25, marginTop: 5}} className={'BillingPeriodOptions'}>
            <div className={'InputHelpText'}>Annually you get 2 months free!</div>
          </div>

          <div className={'MembershipOptions'}>
            <MembershipOption title={`Starter for ${numLocations} location${numLocations == 1 ? '' : 's'}`}
                              price={{amount: listingPrice(results.numLocations), currencyCode: 'USD'}}
                              shortPlanName={'Starter'}
                              highlighted={recommendedPlan() == BusinessSubscriptionType.LISTING}
                              discountedPrice={billingPeriod == 'annual' ? {
                                amount: Math.round(listingPrice(numLocations) * 5.0 / 6.0),
                                currencyCode: 'USD'
                              } : undefined}
                              perfectFor={'Solo practitioners or small business who want to get found ' +
                                  'and grow organically - without needing the Kensu booking platform.'}
                              whatsIncluded={[
                                'Featured business listing in Kensu marketplace',
                                'Appear in location-based search results',
                                'Feature on Kensu social platforms'
                              ]}
                              onClick={() => updateProviderSurveyRpc.send(new ProviderSurveyProto({
                                ...results,
                                chosenSubscription: BusinessSubscriptionType.LISTING
                              }), true)}
            />

            <MembershipOption title={`Growth for ${numPractitioners} practitioner${numPractitioners == 1 ? '' : 's'}`}
                              price={{amount: listingPlusBookingPrice(numPractitioners), currencyCode: 'USD'}}
                              shortPlanName={'Growth'}
                              discountedPrice={billingPeriod == 'annual' ? {
                                amount: Math.round(listingPlusBookingPrice(numPractitioners) * 5.0 / 6.0),
                                currencyCode: 'USD'
                              } : undefined}
                              highlighted={recommendedPlan() == BusinessSubscriptionType.LISTING_PLUS_BOOKING}
                              perfectFor={'Practitioners ready to accept bookings, manage clients, and streamline ' +
                                  'their practice operations through Kensu’s integrated tools.'}
                              whatsIncluded={[
                                'Everything in Starter Plan',
                                'Online booking system',
                                'Automated booking reminders and confirmations',
                                'Secure payment processing',
                                'Client profiles and history'
                              ]}
                              onClick={() => updateProviderSurveyRpc.send(new ProviderSurveyProto({
                                ...results,
                                chosenSubscription: BusinessSubscriptionType.LISTING_PLUS_BOOKING
                              }), true)}
            />

          </div>

        </div>
      </div>
    },
  ]


  /**
   * @param results - the current survey results. this typically uses the react state
   *                  "surveyResults", but you can also pass in a modified once, which is
   *                  necessary when you mutate state (e.g. select a radio button) AND
   *                  want to figure out the next state immediately.
   *
   * @return {int} the next visible step, or the array length if there are no further pages.
   * By returning the array length we know that the
   */
  const getNextVisibleStep = () => {
    for (let i = currentStep + 1; i < surveyPages.length; i++) {
      const page = surveyPages[i];
      // The function may be undefined; if so, defaults to true;
      if (!page.isPageVisible || page.isPageVisible()) {
        return i;
      }
    }

    return surveyPages.length;
  };

  /**
   * @return {int} The previous step or -1 if there is no previous step
   */
  const getPreviousVisibleStep = () => {
    for (let i = currentStep - 1; i >= 0; i--) {
      let page = surveyPages[i];
      if (!page.isPageVisible || page.isPageVisible()) {
        return i;
      }
    }

    return -1;
  };

  const page = surveyPages[currentStep];


  /**
   *
   * @param results the most recent survey results--- make sure not to grab the state
   *                variable if you have mutated it in this frame! pass a new copy.
   *                this is important when using radio buttons
   */
  const advancePage = () => {

    // Check for validation errors
    if (page.validate && !page.validate()) {
      return;
    }

    // We were able to advance the page, clear out this error.
    setValidationError(undefined);

    const nextStep = getNextVisibleStep();

    if (nextStep < surveyPages.length) {
      // There's still another page to go. Advance the step
      setCurrentStep(nextStep);

      // Save the draft. The first page is just a welcome and doesn't send anything
      if (currentStep > 0) {
        updateProviderSurveyRpc.send(results, false);
      }

    } else {
      console.warn('The advance page is not used on the last page! "Try growth" and "Try starter" are custom buttons.')
    }
  };

  const goBackAPage = () => {
    // This just clears out the RPC error when going back
    // setUpdateProfileError('');
    setCurrentStep(getPreviousVisibleStep());
    setValidationError(undefined);
  };

  // This is a null parameter if there is no next step available which hides the button
  const onBackPress = () => {
    goBackAPage();
  };

  // This gets happen on 'OK' press AND 'enter' press in text box.
  const onContinue = () => {
    advancePage();
  };

  return (<>

    { /*progress bar*/}
    <div className={'SurveyProgressBar'}>
      {[...Array(surveyPages.length)].map((_, i) => {
        return (
            <div key={i} className={currentStep > i ?
                'SurveyProgressBarItem SurveyProgressBarItemSelected' :
                'SurveyProgressBarItem SurveyProgressBarItemNotSelected'}/>
        );
      })}
    </div>

    <div className={'VerticalScrollingSection'}>
      <div className={'AppSection WhiteSection'}>
        <div className={'AppSectionContent'}>
          <div className='SurveySection'>

            <div className={'SurveyHeader'}>
              <div className={'SurveyHeaderLeft'}>{page.pageHeaderOverride ?? 'ACCOUNT SETUP'}</div>

              {currentStep > 0 &&
                  // Not every page has a back button      
                  <div className={'SurveyHeaderButtonContainer'}>
                    <ActionButton secondary onClick={() => onBackPress()}>Back</ActionButton>
                  </div>
              }

              {/* There is no continue button on the last page; "Try Growth and Try Starter" are the choices */}
              {(currentStep < surveyPages.length - 1) && <div className={'SurveyHeaderButtonContainer'}>
                <ActionButton onClick={onContinue}>Continue</ActionButton>
              </div>}

            </div>

            {validationError &&
                <div className={'SurveyErrorHeader'}>
                  <div className={'ErrorText'}>{validationError}</div>
                </div>
            }


            {page.title && <span className={'SurveyTitle'}>{page.title}</span>}

            {page.subtitle && <div className={'SurveySubtitle'}>{page.subtitle}</div>}

            {/* This renders the specific widget for the input [checklist, radio, text, etc.] */}
            {page.content}

            {updateProviderSurveyRpc.rpcState.state == 'error' &&
                <div className={'SurveyError'}>{updateProviderSurveyRpc.rpcState.message}</div>
            }

            {/*<pre style={{fontSize: 'smaller'}}>*/}
            {/*  {JSON.stringify(results, null, 2)}*/}
            {/*</pre>*/}
          </div>
        </div>
      </div>
    </div>
  </>);
};

export default BusinessOnboarding;
